import { Link } from "react-router-dom";
import InfoLayout from "@/components/InfoLayout";
import InlineCTA from "@/components/InlineCTA";
import { SERVICES, CITIES, INSURANCES, SITE_URL } from "@/seo/data";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function LeistungenHub() {
  const title = "Autoglas Leistungen | Steinschlag, Scheibentausch, ADAS — Rhein-Neckar";
  const description = "Alle Autoglas-Leistungen für Mannheim, Ludwigshafen, Heidelberg und die Metropolregion Rhein-Neckar: Steinschlagreparatur, Frontscheibentausch, Seiten- und Heckscheibe, ADAS-Kalibrierung.";
  const canonical = `${SITE_URL}/leistungen`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: SERVICES.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: s.name,
      url: `${SITE_URL}/leistung/${s.slug}`,
    })),
  };

  return (
    <InfoLayout title={title} description={description} canonical={canonical} jsonLd={jsonLd}>
      <h1>Unsere Autoglas-Leistungen in der Metropolregion Rhein-Neckar</h1>
      <p className="lead">
        Wir bieten Ihnen das komplette Spektrum moderner Autoglas-Dienstleistungen — vom kleinen Steinschlag
        bis zum kompletten Frontscheibentausch mit ADAS-Kalibrierung. Direktabwicklung mit allen großen
        deutschen Versicherungen, Termin innerhalb 24 Stunden in Mannheim, Ludwigshafen, Heidelberg, Worms,
        Speyer, Weinheim, Schwetzingen und der gesamten Region.
      </p>

      <div className="not-prose grid gap-4 my-8">
        {SERVICES.map((s) => (
          <Link
            key={s.slug}
            to={`/leistung/${s.slug}`}
            className="block bg-white rounded-2xl border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all p-5"
            data-testid={`service-link-${s.slug}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 m-0">{s.name}</h3>
                <p className="text-sm text-slate-600 mt-1 m-0">{s.desc}</p>
                <div className="flex gap-4 mt-3 text-xs">
                  <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-700 font-medium">{s.duration}</span>
                  <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 font-medium">
                    {s.price_from === 0 ? "Mit Teilkasko meist 0 €" : `Ab ${s.price_from} €`}
                  </span>
                </div>
              </div>
              <ArrowRight size={20} className="text-blue-600 mt-1 flex-shrink-0" />
            </div>
          </Link>
        ))}
      </div>

      <h2>Warum unser Autoglas-Service?</h2>
      <ul>
        <li><strong>24-Stunden-Termin</strong> in Mannheim und der gesamten Metropolregion Rhein-Neckar</li>
        <li><strong>Erstausrüsterqualität</strong> (OE/OEM) — Saint-Gobain, Pilkington, AGC, Guardian</li>
        <li><strong>ADAS-Kalibrierung</strong> in einem Arbeitsgang nach Herstellervorgabe</li>
        <li><strong>Direktabwicklung</strong> mit allen großen deutschen Versicherern — null Bürokratie für Sie</li>
        <li><strong>KI-gestützte Preisschätzung</strong> für Selbstzahler nach Fahrzeugschein-Upload</li>
        <li><strong>Bring- und Holservice</strong> auf Wunsch in Mannheim, Ludwigshafen, Heidelberg</li>
        <li><strong>5 Jahre Garantie</strong> auf Dichtigkeit, 2 Jahre auf Materialfehler</li>
      </ul>

      <InlineCTA />

      <h2>Wir arbeiten direkt mit Ihrer Versicherung</h2>
      <p>
        Egal welche Kfz-Versicherung Sie haben — wir rechnen direkt mit dem Versicherer ab. Sie zahlen nur
        Ihre vertraglich vereinbarte Selbstbeteiligung (sofern überhaupt fällig).
      </p>
      <p className="text-sm">
        {INSURANCES.map((i, idx, arr) => (
          <span key={i.slug}>
            <Link to={`/versicherung/${i.slug}`}>{i.name}</Link>{idx < arr.length - 1 ? " · " : ""}
          </span>
        ))}
      </p>

      <h2>Standorte und Anfahrt</h2>
      <p>
        Wir sind für Sie in der gesamten Metropolregion Rhein-Neckar erreichbar — Hauptsitz in Ludwigshafen,
        Servicegebiet umfasst die folgenden Städte:
      </p>
      <p className="text-sm">
        {CITIES.map((c, i, arr) => (
          <span key={c.slug}>
            <Link to={`/standort/${c.slug}`}>{c.name}</Link>{i < arr.length - 1 ? " · " : ""}
          </span>
        ))}
      </p>
    </InfoLayout>
  );
}
